import type { Theme } from './types';
import { readPref } from './prefs';

export const THEMES: { id: Theme; name: string; nameJa: string; color: string }[] = [
  { id: 'lime', name: 'Lime', nameJa: 'ライム', color: '#d6f45b' },
  { id: 'orange', name: 'Orange', nameJa: 'オレンジ', color: '#ff8a3d' },
  { id: 'olive', name: 'Olive', nameJa: 'オリーブ', color: '#b7c24f' },
  { id: 'red', name: 'Red', nameJa: 'レッド', color: '#ff5d52' },
  { id: 'blue', name: 'Blue', nameJa: 'ブルー', color: '#5e9dff' },
];

const KEY = 'vb.theme';

/** Applies a theme to <html> and remembers it so sign-in pages match before /api/me loads. */
export function applyTheme(t: Theme | string | undefined) {
  const theme = THEMES.some((x) => x.id === t) ? (t as Theme) : 'lime';
  document.documentElement.dataset.theme = theme;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* ignore */
  }
}

export function savedTheme(): Theme {
  try {
    const t = localStorage.getItem(KEY);
    if (THEMES.some((x) => x.id === t)) return t as Theme;
  } catch {
    /* ignore */
  }
  return 'lime';
}

// ---- light / dark -------------------------------------------------------------------------
// "system" follows the device (prefers-color-scheme, also in CSS so the first paint is right);
// "light" and "dark" pin it. Kept with the profile's other personal settings (prefs.mode).

export type Mode = 'system' | 'light' | 'dark';
const darkQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

export const savedMode = (): Mode => {
  const m = readPref('mode');
  return m === 'light' || m === 'dark' ? m : 'system';
};

/** Whether the page is dark right now. */
export const isDark = () => document.documentElement.dataset.mode === 'dark' || (!document.documentElement.dataset.mode && !!darkQuery?.matches);

/** Applies a mode to <html> (data-mode, none for system) and the browser's address-bar colour. */
export function applyMode(m: Mode) {
  const root = document.documentElement;
  if (m === 'system') delete root.dataset.mode;
  else root.dataset.mode = m;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', isDark() ? '#0E0E10' : '#ECECEE');
}

// The device switching between light and dark (sunset, Control Centre) updates the address bar too.
darkQuery?.addEventListener('change', () => applyMode(savedMode()));
